import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../api.js';
import { track } from '../events.js';
import { Loading } from './Loading.jsx';
import { Header } from './Header.jsx';
import { BottomNav, SideNav } from './Nav.jsx';
import { HomeView } from './HomeView.jsx';
import { ParamHistorySheet } from './ParamHistorySheet.jsx';
import { LogView } from './LogView.jsx';
import { TasksView } from './TasksView.jsx';
import { SuppliesView } from './SuppliesView.jsx';
import { DoseView } from './DoseView.jsx';
import { TankView } from './TankView.jsx';
import { JournalSection } from './JournalSection.jsx';
import { AddTankFlow } from './AddTankFlow.jsx';
import { TankSettingsSheet } from './TankSettingsSheet.jsx';
import { AccountSheet } from './AccountSheet.jsx';
import { NearView } from './NearView.jsx';
import IcpView from './IcpView.jsx';
import { LivestockBrowser } from './LivestockBrowser.jsx';
import { LivestockHistory } from './LivestockHistory.jsx';
import { TreatmentsView } from './TreatmentsView.jsx';
import { TermsAcceptModal } from './TermsAcceptModal.jsx';
import { TankOverviewGrid } from './TankOverviewGrid.jsx';
import { CoralMark } from './CoralMark.jsx';
import { AllHistoryView } from './AllHistoryView.jsx';
import { ImportReadingsView } from './ImportReadingsView.jsx';
import { usePollingHeartbeat } from '../use-polling-heartbeat.js';
import { useHistoryBackStack, goBackLayers } from '../use-history-back.js';
import {
  openTargetFrom,
  OPEN_ACCOUNT_EVENT,
  OPEN_SETTINGS_EVENT,
  OPEN_ADD_TANK_EVENT,
} from '../app-open.js';
import { rangeCovering } from '../all-history.js';

const TANK_ID_STORAGE_KEY = 'aqs.lastTankId';

function readStoredTankId() {
  try {
    // An accepted tank invite lands on /app/?tank=<id>; open that tank.
    // An id that isn't one of the keeper's falls back to the first tank.
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('tank');
    if (fromUrl) {
      params.delete('tank');
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
      writeStoredTankId(fromUrl);
      return fromUrl;
    }
    return localStorage.getItem(TANK_ID_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}
function writeStoredTankId(id) {
  try {
    if (id) localStorage.setItem(TANK_ID_STORAGE_KEY, id);
    else localStorage.removeItem(TANK_ID_STORAGE_KEY);
  } catch {
    /* private mode etc. — best effort */
  }
}

// The tank the screens may show: only the one picked. The last tank stays in
// state until the picked one's reply arrives, and Home showed it under the
// new pick: a reading saved in that moment went to the last tank.
export function tankForView(tank, tankId) {
  return tank && (!tankId || tank.id === tankId) ? tank : null;
}

export function AppShell({ user, onSignOut, onPageChange, onRefreshUser }) {
  const [tanks, setTanks] = useState(null);
  // Seed from localStorage so a hard refresh stays on whichever tank
  // the user had selected. Cleared on sign-out and when the persisted
  // tank no longer exists (see reloadTanks below).
  const [tankId, setTankIdState] = useState(() => readStoredTankId());
  const tankIdRef = useRef(tankId);
  useEffect(() => {
    tankIdRef.current = tankId;
  }, [tankId]);
  const setTankId = useCallback((id) => {
    setTankIdState(id);
    writeStoredTankId(id);
  }, []);
  // For multi-tank keepers, Home IS the multi-tank grid — that's the
  // landing whenever they tap the Home tab. Tapping a card is a
  // TRANSIENT drill-in for daily ops (logging, editing the tank
  // profile, livestock); tapping Home from the drilled-in view
  // returns to the grid. Not persisted: opening the app always lands
  // on the grid for multi-tank users.
  //
  // Single-tank keepers never have the grid — the single-tank Home is
  // their landing, and this state is harmlessly false.
  const [drilledIn, setDrilledIn] = useState(false);
  const [tank, setTank] = useState(null);
  // The tank id whose own load last failed, for the "could not open" screen.
  const [tankLoadFailed, setTankLoadFailed] = useState(null);
  const [tab, setTabRaw] = useState('home');
  // Wrap setTab to fire a Phase 4 event — gives the admin Behavior
  // panel + retention cohort report a tab-open signal that the
  // server can't see (the tab swap doesn't hit any API).
  const setTab = useCallback((next) => {
    setTabRaw((prev) => {
      if (next !== prev) track('tab.open', next);
      return next;
    });
  }, []);
  const [loading, setLoading] = useState(true);
  const [addingTank, setAddingTank] = useState(false);
  // Account settings is a global (not per-tank) overlay, owned here so
  // every chrome surface — Header, SideNav, the multi-tank grid — can
  // open it in one tap. It used to live buried at the foot of the tank
  // picker, unreachable from the overview entirely.
  // /app/connect/hydros lands with Account open on Connect HYDROS; the
  // address goes back to /app/ so a reload does not reopen it.
  const [accountFocus, setAccountFocus] = useState(() => {
    if (typeof window === 'undefined') return null;
    const target = openTargetFrom(window.location.pathname);
    if (target) window.history.replaceState(null, '', '/app/' + window.location.search);
    return target;
  });
  const [accountOpen, setAccountOpen] = useState(() => Boolean(accountFocus));
  useEffect(() => {
    const onOpen = (e) => {
      setAccountFocus(e.detail || null);
      setAccountOpen(true);
    };
    window.addEventListener(OPEN_ACCOUNT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ACCOUNT_EVENT, onOpen);
  }, []);
  const [nearOpen, setNearOpen] = useState(false);
  // Connect HYDROS's "Add a tank" (feedback #69): Account closes for the
  // add-tank flow and opens again on HYDROS once it is done or cancelled,
  // with the new tank picked for the controller it was added for.
  const [addTankReturn, setAddTankReturn] = useState(null); // { returnTo, thingId }
  const [hydrosLinkTo, setHydrosLinkTo] = useState(null); // { thingId, tankId }
  const closeAccount = () => {
    setAccountOpen(false);
    setAccountFocus(null);
    setHydrosLinkTo(null);
  };
  useEffect(() => {
    const onOpen = (e) => {
      setAccountOpen(false);
      setAccountFocus(null);
      // Near You renders no add-tank flow: leave it, as its own nav does.
      setNearOpen(false);
      setAddTankReturn(e.detail?.returnTo ? e.detail : null);
      setAddingTank(true);
    };
    window.addEventListener(OPEN_ADD_TANK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ADD_TANK_EVENT, onOpen);
  }, []);
  const endAddTank = (created = null) => {
    setAddingTank(false);
    if (!addTankReturn) return;
    setHydrosLinkTo({ thingId: addTankReturn.thingId || null, tankId: created?.id || null });
    setAccountFocus(addTankReturn.returnTo);
    setAccountOpen(true);
    setAddTankReturn(null);
  };
  // Tank settings + Livestock browser are their own pages now, not
  // modals — `view` lets the shell swap the whole main area instead of
  // overlaying. Anything listed here hides the header + bottom-nav so
  // the page owns the full chrome.
  // 'main' | 'settings' | 'livestock' | 'history' | 'icp' | 'treatments' |
  // 'trends' | 'import', and from the Tank tab 'dose' | 'supplies' | 'journal'.
  const [view, setView] = useState('main');
  // All history opened straight after an import starts on a range that
  // shows the import; from anywhere else, on 30 days.
  const [trendsRange, setTrendsRange] = useState(30);
  // The Journal opened from Log's "Note or photo" starts on its note form.
  const [journalCompose, setJournalCompose] = useState(false);
  // Section to land on when settings opens from elsewhere (the Home
  // HYDROS tile's "Pick probes" opens Equipment); cleared on the next open.
  const [settingsSection, setSettingsSection] = useState(null);
  // A card's Edit on Home lands on that parameter's row ({ section, focus }).
  const [settingsFocus, setSettingsFocus] = useState(null);
  const openSettings = () => {
    setSettingsSection(null);
    setSettingsFocus(null);
    setView('settings');
  };
  useEffect(() => {
    const onOpen = (e) => {
      const d = e.detail;
      setSettingsSection((d && typeof d === 'object' ? d.section : d) || null);
      setSettingsFocus((d && typeof d === 'object' ? d.focus : null) || null);
      setView('settings');
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);
  const openLivestock = () => setView('livestock');
  // A page opens at its top, with its way back in sight: Home's supplies
  // Review opened Supplies scrolled down past "Back to Home".
  useEffect(() => {
    if (view !== 'main' && typeof window !== 'undefined') window.scrollTo(0, 0);
  }, [view]);
  // A row on the Tank tab. Equipment and Livestock are Tank settings
  // sections; the rest are pages of their own.
  const openFromTank = (k) => {
    if (k === 'settings') return openSettings();
    if (k === 'equipment' || k === 'livestock') {
      setSettingsSection(k);
      setSettingsFocus(null);
      setView('settings');
      return;
    }
    if (k === 'trends') setTrendsRange(30);
    setJournalCompose(false);
    setView(k);
  };
  // Close affordances route through the browser history so the in-app
  // Close button and the hardware/browser Back button do the same
  // thing — pop the layer. The popstate listener in useHistoryBackStack
  // calls the layer's close(), which sets the state below.
  const closeSettings = () => goBackLayers(1);
  const closeLivestock = () => goBackLayers(1);
  // Param history is a sub-mode of 'main' — it sits in the shell-main
  // slot but keeps the SideNav, Header, and BottomNav visible so it
  // feels like a routed view (not a full-screen takeover). null means
  // no history open; a paramKey string means show that param's history.
  const [historyParam, setHistoryParam] = useState(null);
  // Which Log flow to open on: a Tasks "Upload an ICP result" button
  // lands on the ICP upload instead of the method picker.
  const [logStart, setLogStart] = useState(null);
  const closeHistory = () => goBackLayers(1);

  // Surface the active page to the parent so <FeedbackButton/> knows where
  // we are when the user sends a note.
  useEffect(() => {
    if (!onPageChange) return;
    if (loading || tanks === null) onPageChange('loading');
    else if (tanks && tanks.length === 0) onPageChange('no-tank');
    else onPageChange(tab);
  }, [onPageChange, loading, tanks, tab]);

  const reloadTanks = useCallback(async () => {
    // Always request the enriched summary — the Overview grid needs
    // score + breakdown + latest_reading_ts per tank. The lean shape
    // is fine for our other consumers but the cost is small and the
    // single endpoint keeps the cache hot.
    const list = await api('/api/tanks?summary=1');
    setTanks(list);
    if (list.length > 0) {
      // Validate the persisted tankId is still a real tank. If the user
      // deleted that tank from another device (or this one before a
      // refresh), fall back to the first tank — otherwise reloadTank
      // would 404 forever.
      const stillExists = tankId && list.some((t) => t.id === tankId);
      if (!stillExists) setTankId(list[0].id);
    }
    return list;
  }, [tankId, setTankId]);

  const reloadTank = useCallback(async () => {
    if (!tankId) {
      setTank(null);
      return;
    }
    let t;
    try {
      t = await api(`/api/tanks/${tankId}`);
    } catch (e) {
      if (tankIdRef.current === tankId) setTankLoadFailed(tankId);
      throw e;
    }
    // A slow reply for a tank the keeper has since left must not paint it
    // back over the one picked.
    if (tankIdRef.current !== tankId) return;
    setTankLoadFailed(null);
    setTank(t);
  }, [tankId]);

  useEffect(() => {
    (async () => {
      try {
        await reloadTanks();
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  // Tell the server which timezone this keeper actually lives in, so
  // quiet hours hold overnight mail against their wall clock instead of
  // a default that could silence a west-coast keeper from 6pm. Cached in
  // localStorage and only PUT when it changes, so this is one request on
  // first load and none afterwards — travel or a DST-less move re-syncs
  // it automatically.
  useEffect(() => {
    let tz = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      return;
    }
    if (!tz) return;
    let cached = null;
    try {
      cached = localStorage.getItem('aqs.tz');
    } catch {
      // Private mode / storage disabled — fall through and just send it.
    }
    if (cached === tz) return;
    api('/api/notifications/timezone', { method: 'PUT', body: { timezone: tz } })
      .then(() => {
        try {
          localStorage.setItem('aqs.tz', tz);
        } catch {
          // Nothing to do — we simply re-send on the next load.
        }
      })
      .catch(() => {});
  }, []);

  // Skip the heavy per-tank GET while the keeper is on the Overview
  // — they aren't looking at deep state, and reloadTanks (with score
  // + breakdown) covers what the grid renders. Reload kicks back in
  // the moment they drill into a tank.
  const overviewActive = tanks && tanks.length >= 2 && !drilledIn;
  useEffect(() => {
    if (overviewActive) return;
    reloadTank().catch((e) => {
      // Deleted on another device, or a share the owner took back: back to
      // the tanks the keeper has (reloadTanks re-picks one) and their Home.
      if (e?.status === 404 || e?.status === 403) leaveMissingTank();
    });
  }, [tankId, overviewActive]);

  // Two visibility-gated heartbeats: Overview polls the tanks list
  // (covers stale scores while sitting on the grid); single-tank polls
  // the active tank (covers "I checked HYDROS, came back, why is the
  // card stale"). One shared hook so the visibility/interval machinery
  // doesn't drift between them.
  usePollingHeartbeat(overviewActive, reloadTanks);
  usePollingHeartbeat(!!tankId && !overviewActive, reloadTank);

  // Back on the grid from a tank: refresh it now. Its heartbeat starts with
  // no immediate tick, so a low alk just logged read "Good" there for up to
  // two minutes.
  const wasDrilledIn = useRef(drilledIn);
  useEffect(() => {
    if (wasDrilledIn.current && !drilledIn && tanks?.length >= 2) reloadTanks().catch(() => {});
    wasDrilledIn.current = drilledIn;
  }, [drilledIn]);

  // Browser/hardware Back unwinds the in-app navigation layers instead
  // of leaving the site. Ordered shallow → deep: drilled-into-a-tank
  // (multi-tank only) is the shallowest layer, then the per-tank
  // takeovers (settings / livestock) and the param-history sub-mode.
  // close() functions set state directly — they're what popstate
  // invokes; the public closeSettings/closeHistory above trigger
  // popstate via goBackLayers so in-app Close and browser Back share
  // one path.
  const backLayers = [
    { open: nearOpen, close: () => setNearOpen(false) },
    {
      open: tanks?.length >= 2 && drilledIn,
      close: () => {
        setDrilledIn(false);
        setTab('home');
      },
    },
    { open: view === 'settings', close: () => setView('main') },
    { open: view === 'livestock', close: () => setView('main') },
    { open: view === 'history', close: () => setView('main') },
    { open: view === 'icp', close: () => setView('main') },
    { open: view === 'treatments', close: () => setView('main') },
    { open: view === 'trends', close: () => setView('main') },
    { open: view === 'import', close: () => setView('main') },
    { open: view === 'dose', close: () => setView('main') },
    { open: view === 'supplies', close: () => setView('main') },
    { open: view === 'journal', close: () => setView('main') },
    { open: historyParam != null, close: () => setHistoryParam(null) },
    // Account is a page (owner, 2026-10-21), over whatever was open.
    { open: accountOpen, close: closeAccount },
  ];
  const { resetToBase } = useHistoryBackStack(backLayers);
  const leaveMissingTank = () => {
    resetToBase();
    setDrilledIn(false);
    setTab('home');
    setView('main');
    reloadTanks().catch(() => {});
  };

  // Tapping a primary nav tab from inside Tank Settings / Livestock /
  // param-history should ALSO leave that page (the user-reported
  // "desktop nav doesn't work in Tank Settings" bug). Tapping Home from
  // a drilled-in tank returns to the multi-tank grid — resetToBase()
  // pops every open layer via the browser history so Back stays in
  // sync. Other tabs (Plan / Tasks / Dose) keep the per-tank context so
  // the keeper can log a reading on the tank they just opened.
  const setTabAndShowMain = (next) => {
    setLogStart(null);
    setTab(next);
    setHistoryParam(null);
    if (next === 'home') {
      resetToBase();
      setDrilledIn(false);
    }
    setView('main');
  };

  const signOut = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch {}
    // Don't carry the previous user's tank selection into the next
    // session — especially on shared devices.
    writeStoredTankId(null);
    onSignOut();
  };

  if (loading || tanks === null) return <Loading />;

  // Near You is a full page, not a modal — it owns the main column like
  // every other tab, with the nav rails still visible so the keeper can
  // step back out in one tap. Handled here (before the layout branches)
  // so it renders the same from single-tank Home and the multi-tank
  // grid. Tapping a nav tab from here closes Near and lands on that tab.
  const leaveNear = (next) => {
    setNearOpen(false);
    if (!next) return;
    if (tanks.length >= 2 && next !== 'home') setDrilledIn(true);
    setTabAndShowMain(next);
  };
  // Account is a page (owner, 2026-10-21): sign-in, notifications, Siri,
  // connected services and delete were one long sheet. It opens over
  // whatever was showing, which comes back when it closes.
  const leaveAccount = (next) => {
    closeAccount();
    setNearOpen(false);
    if (!next) return;
    if (tanks.length >= 2 && next !== 'home') setDrilledIn(true);
    setTabAndShowMain(next);
  };
  const nearFromAccount = () => {
    closeAccount();
    setNearOpen(true);
  };
  if (accountOpen) {
    return (
      <div className="shell">
        <SideNav
          tab={null}
          setTab={leaveAccount}
          user={user}
          onSignOut={signOut}
          onOpenAccount={() => {}}
          onOpenNear={nearFromAccount}
          accountActive
        />
        <div className="shell-col">
          <main className="shell-main">
            <AccountSheet
              focus={accountFocus}
              linkTo={hydrosLinkTo}
              onClose={() => goBackLayers(1)}
              onSignOut={signOut}
            />
          </main>
          {tanks.length > 0 && (
            <BottomNav tab={null} setTab={leaveAccount} onOpenNear={nearFromAccount} />
          )}
        </div>
        {user?.needs_terms_accept && (
          <TermsAcceptModal
            user={user}
            onAccepted={async () => {
              if (typeof onRefreshUser === 'function') await onRefreshUser();
            }}
            onSignOut={signOut}
          />
        )}
      </div>
    );
  }

  if (nearOpen) {
    return (
      <div className="shell">
        <SideNav
          tab={tab}
          setTab={leaveNear}
          user={user}
          onSignOut={signOut}
          onOpenAccount={() => setAccountOpen(true)}
          onOpenNear={() => setNearOpen(true)}
          nearActive
        />
        <div className="shell-col">
          <main className="shell-main">
            <NearView user={user} tank={tank} onClose={() => setNearOpen(false)} />
          </main>
          <BottomNav tab={tab} setTab={leaveNear} onOpenNear={() => setNearOpen(true)} nearActive />
        </div>
      </div>
    );
  }

  // Edge case: user completed onboarding but deleted their only tank.
  // Offer the add-tank flow directly from the empty state.
  if (tanks.length === 0) {
    return (
      <div className="shell">
        <SideNav
          tab={tab}
          setTab={setTab}
          user={user}
          onSignOut={signOut}
          onOpenNear={() => setNearOpen(true)}
        />
        <div className="shell-col">
          <Header
            tank={null}
            tanks={tanks}
            onPickTank={() => {}}
            onSignOut={signOut}
            onAddTank={() => setAddingTank(true)}
          />
          <main className="shell-main">
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <CoralMark size={44} style={{ marginBottom: 2 }} />
              <div
                className="font-heading"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.3em',
                  color: 'var(--text-dim)',
                  marginBottom: 14,
                }}
              >
                SENSEI
              </div>
              <p style={{ fontSize: 16, lineHeight: 1.55, marginBottom: 20 }}>
                You don't have a tank on file anymore.
              </p>
              <button
                onClick={() => setAddingTank(true)}
                className="font-heading"
                style={{
                  padding: '12px 20px',
                  borderRadius: 12,
                  background: 'var(--cyan)',
                  color: 'var(--cyan-text)',
                  fontSize: 13,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                + Add a tank
              </button>
            </div>
          </main>
        </div>
        {addingTank && (
          <AddTankFlow
            tanks={tanks}
            onCancel={() => endAddTank()}
            onDone={async (created) => {
              setAddingTank(false);
              await reloadTanks();
              setTankId(created.id);
              setTab('home');
              endAddTank(created);
            }}
          />
        )}
      </div>
    );
  }

  // Multi-tank keepers: the Home tab IS the grid. They land here on
  // every sign-in and every Home tap. Drilling into a card is
  // transient — the next Home tap brings them straight back here, no
  // toggle, no remembered preference. Single-tank keepers skip this
  // branch entirely and see their familiar single-tank Home.
  const showOverview = tanks.length >= 2 && !drilledIn && tab === 'home' && view === 'main';
  if (showOverview) {
    return (
      <div className="shell">
        <SideNav
          tab="home"
          setTab={(next) => {
            // Any tab but Home opens a tank: on the grid none is loaded.
            if (next !== 'home') setDrilledIn(true);
            setTabAndShowMain(next);
          }}
          user={user}
          onSignOut={signOut}
          onOpenAccount={() => setAccountOpen(true)}
          onOpenNear={() => setNearOpen(true)}
        />
        <div className="shell-col">
          <main className="shell-main">
            <TankOverviewGrid
              tanks={tanks}
              onPickTank={(id) => {
                setTankId(id);
                setDrilledIn(true);
                setTab('home');
              }}
              onAddTank={() => setAddingTank(true)}
              onOpenAccount={() => setAccountOpen(true)}
              user={user}
            />
          </main>
          {/* The grid IS Home, so the bottom nav stays visible on mobile
              here too (it was dropped when the grid landing shipped).
              Home keeps the grid; any other tab drills into the active
              tank so the per-tank view has a tank to render. */}
          <BottomNav
            tab="home"
            setTab={(next) => {
              if (next !== 'home') setDrilledIn(true);
              setTabAndShowMain(next);
            }}
            onOpenNear={() => setNearOpen(true)}
          />
        </div>
        {addingTank && (
          <AddTankFlow
            tanks={tanks}
            onCancel={() => endAddTank()}
            onDone={async (created) => {
              setAddingTank(false);
              await reloadTanks();
              setTankId(created.id);
              setDrilledIn(true);
              setTab('home');
              endAddTank(created);
            }}
          />
        )}
        {user?.needs_terms_accept && (
          <TermsAcceptModal
            user={user}
            onAccepted={async () => {
              if (typeof onRefreshUser === 'function') await onRefreshUser();
            }}
            onSignOut={signOut}
          />
        )}
      </div>
    );
  }

  if (!tankForView(tank, tankId)) {
    // A failed load is said, with a way out: the full-screen Loading has no
    // navigation, and a Home Screen app no Back gesture.
    if (tankLoadFailed && tankLoadFailed === tankId) {
      return (
        <TankLoadFailed
          onRetry={() => reloadTank().catch(() => {})}
          onAllTanks={tanks.length >= 2 ? leaveMissingTank : null}
        />
      );
    }
    return <Loading />;
  }

  // A page opened from the Tank tab goes back to it.
  const backTo = tab === 'tank' ? 'Tank' : tab === 'log' ? 'Log' : 'Home';

  return (
    <div className="shell">
      <SideNav
        tab={tab}
        setTab={setTabAndShowMain}
        user={user}
        onSignOut={signOut}
        onOpenAccount={() => setAccountOpen(true)}
        onOpenNear={() => setNearOpen(true)}
      />
      <div className="shell-col">
        {view === 'main' && (
          <Header
            tank={tank}
            tanks={tanks}
            onPickTank={(id) => setTankId(id)}
            onBackToOverview={
              tanks.length >= 2
                ? () => {
                    resetToBase();
                    setDrilledIn(false);
                    setTab('home');
                  }
                : null
            }
            onSignOut={signOut}
            onAddTank={() => setAddingTank(true)}
            onOpenSettings={openSettings}
            onOpenAccount={() => setAccountOpen(true)}
          />
        )}
        <main className="shell-main">
          {view === 'settings' && tank && (
            <TankSettingsSheet
              tank={tank}
              user={user}
              backLabel={backTo}
              initialSection={settingsSection}
              onSectionChange={setSettingsSection}
              initialFocus={settingsFocus}
              onClose={closeSettings}
              onReload={async () => {
                await reloadTank();
              }}
              onTankDeleted={async () => {
                const remaining = await reloadTanks();
                setTankId(remaining?.[0]?.id || null);
                setTab('home');
                closeSettings();
              }}
            />
          )}
          {view === 'livestock' && tank && (
            <LivestockBrowser
              tank={tank}
              onClose={closeLivestock}
              onReload={reloadTank}
              backLabel={backTo}
            />
          )}
          {view === 'history' && tank && (
            <LivestockHistory
              key={tank.id}
              tank={tank}
              onClose={() => goBackLayers(1)}
              backLabel={backTo}
            />
          )}
          {view === 'icp' && tank && (
            <IcpView
              key={tank.id}
              tank={tank}
              onClose={() => goBackLayers(1)}
              onAddReport={() => {
                setTabAndShowMain('log');
                setLogStart('icp');
              }}
              backLabel={backTo}
            />
          )}
          {view === 'treatments' && tank && (
            <TreatmentsView
              key={tank.id}
              tank={tank}
              onClose={() => goBackLayers(1)}
              onReload={reloadTank}
              backLabel={backTo}
            />
          )}
          {view === 'trends' && !historyParam && tank && (
            <AllHistoryView
              key={tank.id}
              tank={tank}
              onClose={() => goBackLayers(1)}
              onOpenParam={setHistoryParam}
              onOpenImport={tank.role === 'viewer' ? null : () => setView('import')}
              initialRangeDays={trendsRange}
            />
          )}
          {view === 'import' && tank && (
            <ImportReadingsView
              key={tank.id}
              tank={tank}
              onClose={() => goBackLayers(1)}
              onOpenAllHistory={(firstTs) => {
                setTrendsRange(firstTs ? rangeCovering(firstTs) : 30);
                setView('trends');
              }}
              onReload={reloadTank}
            />
          )}
          {view === 'dose' && tank && (
            <PageBack label={backTo} onBack={() => goBackLayers(1)}>
              <DoseView tank={tank} onReload={reloadTank} />
            </PageBack>
          )}
          {view === 'supplies' && tank && (
            <PageBack label={backTo} onBack={() => goBackLayers(1)}>
              <SuppliesView tank={tank} user={user} onReload={reloadTank} />
            </PageBack>
          )}
          {view === 'journal' && tank && (
            <PageBack label={backTo} onBack={() => goBackLayers(1)}>
              <div style={{ padding: '0 20px' }}>
                <JournalSection
                  key={tank.id}
                  tank={tank}
                  onReload={reloadTank}
                  asPage
                  compose={journalCompose}
                />
              </div>
            </PageBack>
          )}
          {(view === 'main' || view === 'trends') && historyParam && tank && (
            <ParamHistorySheet
              tank={tank}
              paramKey={historyParam}
              readings={(tank.readings || []).filter((r) => r.param === historyParam)}
              onClose={closeHistory}
              onReload={reloadTank}
            />
          )}
          {view === 'main' && !historyParam && tab === 'home' && (
            <HomeView
              tank={tank}
              onTab={(next) =>
                next === 'supplies' || next === 'dose' ? setView(next) : setTab(next)
              }
              onReload={reloadTank}
              onOpenHistory={setHistoryParam}
              user={user}
              onRefreshUser={onRefreshUser}
              onOpenTankSettings={openSettings}
              onOpenLivestock={openLivestock}
              onOpenLivestockHistory={() => setView('history')}
              onOpenIcp={() => setView('icp')}
              onOpenAllHistory={() => {
                setTrendsRange(30);
                setView('trends');
              }}
              onOpenImport={tank.role === 'viewer' ? null : () => setView('import')}
              onOpenTreatments={() => setView('treatments')}
              onOpenAccount={() => setAccountOpen(true)}
            />
          )}
          {view === 'main' && tab === 'log' && (
            <LogView
              key={logStart || 'picker'}
              tank={tank}
              initialMethod={logStart}
              onSaved={() => reloadTank().catch(() => {})}
              onOpenHistory={(param) => {
                setTab('home');
                setHistoryParam(param);
              }}
              onOpenJournal={() => {
                setJournalCompose(true);
                setView('journal');
              }}
              onOpenTreatments={() => setView('treatments')}
              onOpenCalculator={() => setView('dose')}
              onDone={() => {
                // Saved already; a failed refresh shows on Home, never as
                // an unhandled rejection.
                reloadTank().catch(() => {});
                setTab('home');
              }}
              onCancel={() => setTab('home')}
              onOpenIcp={() => {
                // Leaving the Log tab for Home, so Back from the results
                // page lands on Home, not on a finished Log flow.
                reloadTank().catch(() => {});
                setTab('home');
                setView('icp');
              }}
              onOpenImport={
                tank.role === 'viewer'
                  ? null
                  : () => {
                      setTab('home');
                      setView('import');
                    }
              }
            />
          )}
          {view === 'main' && tab === 'tank' && <TankView tank={tank} onOpen={openFromTank} />}
          {view === 'main' && tab === 'tasks' && (
            <TasksView
              tank={tank}
              onReload={reloadTank}
              onOpenLog={() => setTabAndShowMain('log')}
              onOpenIcp={() => {
                setTabAndShowMain('log');
                setLogStart('icp');
              }}
            />
          )}
        </main>
        {/* Pages keep the bottom bar (CLAUDE.md, "Pages are pages"):
            Settings and Livestock left the keeper with only a Back button. */}
        {(view === 'main' ||
          view === 'settings' ||
          view === 'livestock' ||
          view === 'icp' ||
          view === 'history' ||
          view === 'treatments' ||
          view === 'trends' ||
          view === 'import' ||
          view === 'dose' ||
          view === 'supplies' ||
          view === 'journal') && (
          <BottomNav tab={tab} setTab={setTabAndShowMain} onOpenNear={() => setNearOpen(true)} />
        )}
      </div>
      {addingTank && (
        <AddTankFlow
          tanks={tanks}
          onCancel={() => endAddTank()}
          onDone={async (created) => {
            setAddingTank(false);
            await reloadTanks();
            setTankId(created.id);
            // On the new tank's Home: a second tank turns Home into the grid,
            // and a tank left under Tank settings sat on Loading with no
            // way out (review of UI step 3).
            setDrilledIn(true);
            setView('main');
            setTab('home');
            endAddTank(created);
          }}
        />
      )}
      {user?.needs_terms_accept && (
        <TermsAcceptModal
          user={user}
          onAccepted={async () => {
            if (typeof onRefreshUser === 'function') await onRefreshUser();
          }}
          onSignOut={signOut}
        />
      )}
    </div>
  );
}

function TankLoadFailed({ onRetry, onAllTanks }) {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 16,
        textAlign: 'center',
        color: 'var(--text)',
      }}
    >
      <p style={{ margin: 0, fontSize: 17 }}>This tank did not open. Check your connection.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button type="button" className="ts-chip" style={{ minHeight: 44 }} onClick={onRetry}>
          Try again
        </button>
        {onAllTanks && (
          <button type="button" className="ts-chip" style={{ minHeight: 44 }} onClick={onAllTanks}>
            All tanks
          </button>
        )}
      </div>
    </div>
  );
}

// Supplies, Dosing and the Journal were a tab or a Home section; opened from
// the Tank tab they are pages with a way back (a Home Screen app has no Back
// gesture). Each keeps its own title.
function PageBack({ label, onBack, children }) {
  return (
    <div className="lb-page">
      <div className="pg-back-bar">
        <button
          type="button"
          className="ts-back"
          aria-label={`Back to ${label.toLowerCase()}`}
          onClick={onBack}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{label}</span>
        </button>
      </div>
      {children}
    </div>
  );
}
